import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { subtitleLangCodes } from 'lib/lang';

import type { Options as PlyrOptions } from 'plyr';

const defaultVideoControls = [
  'play-large',
  'play',
  'rewind',
  'fast-forward',
  'progress',
  'current-time',
  'mute',
  'volume',
  'settings',
  'pip',
  'fullscreen'
];

interface Props {
  borderRadius?: string;
  controls?: string[];
  disableControls?: boolean;
  getPlayer?: (player: Plyr) => void;
  jumpToTime?: number;
  onEnded?: (player: Plyr) => void;
  onError?: () => void;
  onLoadedMetadata?: () => void;
  onPause?: (player: Plyr) => void;
  onPlaying?: (player: Plyr) => void;
  onRatechange?: (player: Plyr) => void;
  onReady?: (player: Plyr) => void;
  onSeeking?: (player: Plyr) => void;
  onTimeupdate?: (player: Plyr) => void;
  poster?: string;
  provider?: string;
  subtitles?: { subtitleLanguage: string; subtitleUrl: string }[];
  url?: string;
  videoId?: string;
  videoHash?: string;
}

const Video = ({
  borderRadius,
  controls,
  disableControls,
  getPlayer,
  jumpToTime,
  onEnded,
  onError,
  onLoadedMetadata,
  onPause,
  onPlaying,
  onRatechange,
  onReady,
  onSeeking,
  onTimeupdate,
  poster,
  provider,
  subtitles,
  url,
  videoId,
  videoHash
}: Props) => {
  const {
    messages,
    pageState: { readerCourseData },
    theme
  } = useReduxState();

  const containerElement = useRef<HTMLDivElement>(undefined);
  const player = useRef<Plyr>(undefined);
  const videoElement = useRef<HTMLDivElement | HTMLVideoElement>(undefined);

  const [dreambrokerUseHd, setDreambrokerUseHd] = useState(false);
  const [dreambrokerUseFullHd, setDreambrokerUseFullHd] = useState(false);
  const [ready, setReady] = useState(!(provider === 'dreambroker' && videoId));

  useEffect(() => {
    if (provider === 'dreambroker' && videoId) checkDreambrokerHdSupport();

    return () => {
      if (player.current) player.current.destroy();
    };
  }, []);

  useEffect(() => {
    if (ready) initPlyr();
  }, [ready]);

  useEffect(() => {
    if (jumpToTime && player.current) player.current.currentTime = jumpToTime;
  }, [player.current, jumpToTime]);

  const checkDreambrokerHdSupport = () => {
    const video = document.createElement('video');

    video.onloadedmetadata = () => checkDreambrokerFullHdSupport();
    video.onerror = () => setReady(true);

    video.src = `https://dreambroker.com/channel${videoId}/get/hd.mp4`;
  };

  const checkDreambrokerFullHdSupport = () => {
    const video = document.createElement('video');

    video.onloadedmetadata = () => {
      setDreambrokerUseHd(true);
      setDreambrokerUseFullHd(true);
      setReady(true);
    };
    video.onerror = () => {
      setDreambrokerUseHd(true);
      setReady(true);
    };

    video.src = `https://dreambroker.com/channel${videoId}/get/fullhd.mp4`;
  };

  const onEnterFullscreen = () => {
    try {
      // we need to keep track if this video was the one that was in
      // fullscreen because there may be many and plyr fires the
      // exitfullscreen event for all videos even if only one of them
      // was in fullscreen
      //
      // we could use component state but for some reason exising fullscreen
      // seems to lose component state, which is why we use session storage
      sessionStorage.setItem('vuoVideoFullscreenId', `${url}-${videoId}`);
    } catch {}
  };

  const onExitFullscreen = () => {
    let fullscreenId = null;
    try {
      fullscreenId = sessionStorage.vuoVideoFullscreenId;
    } catch {}
    if (
      fullscreenId === `${url}-${videoId}` &&
      containerElement.current?.scrollIntoView
    )
      containerElement.current.scrollIntoView();
  };

  const initPlyr = async () => {
    const Plyr = (await import('plyr')).default;

    // define maximum playback speed based on course config, if relevant
    let playbackSpeeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5];
    if (
      readerCourseData?.course?.completionEnabled &&
      readerCourseData?.course?.completionVideos > 0
    ) {
      const maxSpeed =
        readerCourseData?.course?.completionVideoMaxPlaybackSpeed || 2.5;
      playbackSpeeds = playbackSpeeds.filter((speed) => speed <= maxSpeed);
    }

    const options: PlyrOptions = {
      captions: {
        active: true,
        language: subtitles?.[0]?.subtitleLanguage || 'auto'
      },
      controls: defaultVideoControls,
      i18n: {
        captions: messages.videoCaptions,
        disabled: messages.commonDisabled,
        normal: messages.commonNormal,
        speed: messages.videoSpeed,
        quality: messages.videoQuality
      },
      speed: {
        selected: 1,
        options: playbackSpeeds
      }
    };

    if (controls) options.controls = controls;

    if (dreambrokerUseFullHd) {
      options.quality = { default: 1080, options: [] };
    } else if (dreambrokerUseHd) {
      options.quality = { default: 720, options: [] };
    }

    const myPlayer = new Plyr(videoElement.current, options);

    // event listeners
    if (onEnded) myPlayer.on('ended', () => onEnded(myPlayer));
    if (onPause) myPlayer.on('pause', () => onPause(myPlayer));
    if (onPlaying) myPlayer.on('playing', () => onPlaying(myPlayer));
    if (onRatechange) myPlayer.on('ratechange', () => onRatechange(myPlayer));
    if (onReady) myPlayer.on('ready', () => onReady(myPlayer));
    if (onSeeking) myPlayer.on('seeking', () => onSeeking(myPlayer));
    if (onTimeupdate) myPlayer.on('timeupdate', () => onTimeupdate(myPlayer));
    myPlayer.on('enterfullscreen', () => onEnterFullscreen());
    myPlayer.on('exitfullscreen', () => onExitFullscreen());

    // Plyr asks Vimeo once not to draw its own captions, but Vimeo sometimes
    // draws them anyway, showing them twice. Repeat the request on playback and
    // on caption toggle.
    if (provider === 'vimeo') {
      const hideVimeoCaptions = () => {
        if (!myPlayer.embed) return;
        if (myPlayer.language) {
          // Have Vimeo not render captions, but have Plyr receive them
          myPlayer.embed
            .enableTextTrack(myPlayer.language, null, false)
            .catch(() => {});
        } else {
          // If captions are turned off, turn Vimeo's track off too
          myPlayer.embed.disableTextTrack().catch(() => {});
        }
      };
      myPlayer.on('playing', hideVimeoCaptions);
      myPlayer.on('captionsenabled', hideVimeoCaptions);
      myPlayer.on('captionsdisabled', hideVimeoCaptions);
    }

    player.current = myPlayer;
    if (getPlayer) getPlayer(player.current);
  };

  /**
   * Some browsers have a native player in fullscreen mode that
   * bypasses Plyr controls. Make sure rate change limit isn't
   * bypassed by following the video ratechange events
   */
  const handleRatelimit = (event) => {
    const maxSpeed =
      readerCourseData?.course?.completionVideoMaxPlaybackSpeed || 2.5;
    if (event?.target?.playbackRate && event?.target?.playbackRate > maxSpeed) {
      event.target.playbackRate = maxSpeed;
      player.current.speed = maxSpeed;
    }
  };

  const setElementRef = (el: HTMLDivElement) => {
    containerElement.current = el;
  };

  const setVideoElementRef = (el: HTMLVideoElement | HTMLDivElement) => {
    if (videoElement.current?.removeEventListener) {
      videoElement.current.removeEventListener('ratechange', handleRatelimit);
    }
    videoElement.current = el;
    if (el?.addEventListener) {
      videoElement.current.addEventListener('ratechange', handleRatelimit);
    }
  };

  if (!ready) return null;

  return (
    <div className="video" ref={setElementRef}>
      {url ? (
        <video
          ref={setVideoElementRef}
          className="video-plyr"
          controls={true}
          crossOrigin="anonymous"
          onLoadedMetadata={onLoadedMetadata}
          preload="metadata"
          {...(poster ? { poster } : {})}
        >
          <source onError={onError} src={url} type="video/mp4" />

          {subtitles?.length
            ? subtitles.map((subtitle, subtitleIndex) => (
                <track
                  key={subtitleIndex}
                  kind="subtitles"
                  label={
                    subtitleLangCodes[subtitle.subtitleLanguage] ||
                    subtitleLangCodes.en
                  }
                  src={subtitle.subtitleUrl}
                  srcLang={subtitle.subtitleLanguage || 'en'}
                  default={true}
                />
              ))
            : null}
        </video>
      ) : provider === 'dreambroker' && videoId ? (
        <video
          ref={setVideoElementRef}
          className="video-plyr"
          controls={true}
          preload="metadata"
        >
          {dreambrokerUseFullHd ? (
            <source
              src={`https://dreambroker.com/channel${videoId}/get/fullhd.mp4`}
              type="video/mp4"
              // @ts-ignore
              size={1080}
            />
          ) : null}

          {dreambrokerUseHd ? (
            <source
              src={`https://dreambroker.com/channel${videoId}/get/hd.mp4`}
              type="video/mp4"
              // @ts-ignore
              size={720}
            />
          ) : null}

          <source
            src={`https://dreambroker.com/channel${videoId}/get/normal.mp4`}
            type="video/mp4"
            // @ts-ignore
            size={480}
          />
        </video>
      ) : provider && videoId ? (
        <div
          ref={setVideoElementRef}
          className="video-plyr"
          data-plyr-provider={provider}
          data-plyr-embed-id={videoId}
          {...(videoHash ? { 'data-plyr-embed-hash': videoHash } : {})}
        />
      ) : null}

      <style jsx>{`
        .video :global(video) {
          max-width: 100%;
        }
        .video :global(.plyr),
        .video :global(.plyr__video-embed iframe) {
          border-radius: ${borderRadius || theme.cardBorderRadius};
        }
        .video :global(input[type='range']:active::-webkit-slider-thumb),
        .video :global(input[type='range']:active::-moz-range-thumb),
        .video :global(input[type='range']:active::-ms-thumb) {
          background: ${theme.mainColor};
        }
        .video :global(.plyr--video .plyr__controls .plyr__control:hover),
        .video
          :global(.plyr--video .plyr__controls .plyr__control.plyr__tab-focus),
        .video
          :global(
            .plyr--video .plyr__controls .plyr__control[aria-expanded='true']
          ),
        .video
          :global(
            .plyr__menu__container
              label.plyr__control
              input[type='radio']:checked
              + span
          ),
        .video
          :global(
            .plyr__menu__container
              .plyr__control[role='menuitemradio'][aria-checked='true']::before
          ),
        .video :global(.plyr__control--overlaid) {
          background: ${theme.mainColor};
        }
        .video :global(.plyr--video .plyr__control) {
          border-radius: ${theme.buttonBorderRadius};
        }
        .video :global(.plyr--video .plyr__control.plyr__control--overlaid) {
          border-radius: 100%;
        }
        .video :global(.plyr--video .plyr__control:hover) {
          background: ${theme.mainColorActive};
        }
        .video :global(.plyr__progress input[type='range']),
        .video :global(.plyr__volume input[type='range']) {
          color: ${theme.mainColorActive};
        }
        .video :global(.plyr__controls) {
          display: ${disableControls ? 'none' : 'flex'};
        }
        .video :global(.plyr__control) {
          display: ${disableControls ? 'none' : 'flex'};
        }
      `}</style>
    </div>
  );
};

export default Video;
